import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import {
    ArrowUpRight,
    Facebook,
    Instagram,
    X
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { BrandLogo } from '@/components/generals/brand-logo'
import { siteConfig, primaryNav, experienceLinks, socialLinks } from '@/constants/site'
import { packageItems } from '@/constants/packages'
import { roomDetails } from '@/constants/rooms'

interface HeaderProps {
    /** Sit transparently over a dark full-bleed hero until the reader scrolls. */
    transparent?: boolean
}

const socialIcons = {
    Facebook,
    Instagram
}

const socials = socialLinks.map((link) => ({
    label: link.label,
    href: link.href,
    Icon: socialIcons[link.label as keyof typeof socialIcons]
}))

const columnLabel = 'text-[13px] font-medium uppercase tracking-[0.14em] text-ink-soft md:text-[15px]'

export const Header: FC<HeaderProps> = ({ transparent = false }): ReactNode => {
    const [open, setOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const router = useRouter()

    useEffect(() => {
        setOpen(false)
    }, [router.asPath])

    useEffect(() => {
        if (!transparent) return

        const onScroll = (): void => setScrolled(window.scrollY > 40)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => {
            window.removeEventListener('scroll', onScroll)
        }
    }, [transparent, router.asPath])

    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        document.addEventListener('keydown', onKey)
        document.body.style.overflow = 'hidden'

        return () => {
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = ''
        }
    }, [open])

    // The bar only stays see-through while the page is at the top of a dark
    // hero and the menu is closed; anything else needs the cream plate to keep
    // the wordmark legible.
    const overHero = transparent && !scrolled && !open

    return (
        <>
            <header
                className={cn(
                    'fixed inset-x-0 top-0 z-50 transition-colors duration-500',
                    overHero ? 'bg-transparent' : 'bg-cream'
                )}
            >
                <div className='relative flex h-[72px] items-center justify-between px-5 md:h-[84px] md:px-10 lg:px-[60px]'>
                    <button
                        type='button'
                        onClick={() => setOpen((value) => !value)}
                        aria-expanded={open}
                        aria-controls='primary-menu'
                        className={cn(
                            'group relative z-10 flex items-center gap-3 transition-colors duration-500',
                            overHero ? 'text-cream' : 'text-ink'
                        )}
                    >
                        {open ? (
                            <X className='size-[26px]' aria-hidden='true' />
                        ) : (
                            <span aria-hidden='true' className='flex w-[30px] flex-col gap-[7px]'>
                                {[0, 1].map((bar) => (
                                    <span
                                        key={bar}
                                        className='block h-[2px] w-full bg-current transition-transform duration-300 group-hover:scale-x-90'
                                    />
                                ))}
                            </span>
                        )}

                        <span className='t-label text-current'>
                            {open ? 'Close' : 'Menu'}
                        </span>
                    </button>

                    <Link
                        href='/'
                        className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
                        aria-label={`${siteConfig.name} home`}
                    >
                        {/* Over a dark hero the wordmark reads in cream; on the cream plate the full-colour logo takes over. */}
                        <span
                            className={cn(
                                'hidden whitespace-nowrap font-heading text-[26px] font-medium leading-none tracking-[-0.03em] text-cream transition-opacity duration-500 sm:block md:text-[32px]',
                                overHero ? 'opacity-100' : 'opacity-0'
                            )}
                        >
                            {siteConfig.name}
                        </span>

                        <span
                            className={cn(
                                'absolute left-1/2 top-1/2 block -translate-x-1/2 -translate-y-1/2 transition-opacity duration-500',
                                overHero ? 'opacity-0' : 'opacity-100'
                            )}
                        >
                            <BrandLogo />
                        </span>
                    </Link>

                    <div className='flex items-center gap-2'>
                        <Link
                            href='/contact'
                            className={cn(
                                'group relative inline-flex items-center overflow-hidden rounded-full border px-6 py-3 text-[16px] font-medium leading-none transition-[color,border-color] duration-500 active:scale-95 md:px-7 md:py-3.5 md:text-[18px]',
                                overHero
                                    ? 'border-cream/70 bg-transparent text-cream hover:text-ink'
                                    : 'border-brand bg-brand text-cream hover:border-ink'
                            )}
                        >
                            <span
                                aria-hidden='true'
                                className={cn(
                                    'absolute inset-0 origin-left scale-x-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-x-100',
                                    overHero ? 'bg-cream' : 'bg-ink'
                                )}
                            />

                            {/* Shorter label on phones leaves room for the centred logo. */}
                            <span className='relative z-10 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-x-1'>
                                <span className='sm:hidden'>
                                    Book now
                                </span>

                                <span className='hidden sm:inline'>
                                    Book your stay
                                </span>
                            </span>

                            <span className='relative z-10 flex w-0 items-center justify-center overflow-hidden opacity-0 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:ml-2 group-hover:w-4 group-hover:opacity-100'>
                                <ArrowUpRight className='size-4 shrink-0' />
                            </span>
                        </Link>
                    </div>
                </div>
            </header>

            <div
                id='primary-menu'
                aria-hidden={!open}
                className={cn(
                    'fixed inset-y-0 left-0 z-40 w-full overflow-y-auto overscroll-contain bg-cream transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] md:w-[calc(100%-20px)] md:rounded-r-[20px]',
                    open ? 'translate-x-0' : '-translate-x-full'
                )}
            >
                <div className='flex min-h-full flex-col px-5 pb-14 pt-[100px] md:px-10 md:pb-14 md:pt-[130px] lg:px-[60px]'>
                    <div className='grid gap-10 lg:grid-cols-[1.2fr_1fr]'>
                        <div>
                            <p className={columnLabel}>
                                Menu
                            </p>

                            <ul className='mt-5 flex flex-col'>
                                {primaryNav.map((item, index) => (
                                    <li key={item.href} className='overflow-hidden'>
                                        <Link
                                            href={item.href}
                                            tabIndex={open ? 0 : -1}
                                            className={cn(
                                                // Sized against the viewport height so all seven entries fit
                                                // a laptop screen without scrolling, and still read large on
                                                // a tall display.
                                                'block font-heading text-[40px] font-medium leading-[1.02] tracking-[-0.04em] text-brand transition-[transform,opacity,color] duration-500 hover:text-ink md:text-[clamp(44px,6.4vh,76px)]',
                                                open ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
                                            )}
                                            style={{ transitionDelay: open ? `${120 + index * 55}ms` : '0ms' }}
                                        >
                                            {item.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div
                            className={cn(
                                'transition-opacity duration-500',
                                open ? 'opacity-100' : 'opacity-0'
                            )}
                            style={{ transitionDelay: open ? '400ms' : '0ms' }}
                        >
                            <p className={columnLabel}>
                                {siteConfig.name}
                            </p>

                            <ul className='mt-5 flex flex-col gap-2'>
                                {experienceLinks.map((item) => (
                                    <li key={item.label}>
                                        <Link
                                            href={item.href}
                                            tabIndex={open ? 0 : -1}
                                            className='text-[22px] font-medium leading-[1.5] text-brand underline underline-offset-[6px] transition-colors hover:text-ink md:text-[26px]'
                                        >
                                            {item.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>

                            <ul className='mt-8 flex gap-2'>
                                {socials.map(({ label, href, Icon }) => (
                                    <li key={label}>
                                        <a
                                            href={href}
                                            target='_blank'
                                            rel='noreferrer'
                                            aria-label={label}
                                            tabIndex={open ? 0 : -1}
                                            className='flex size-[52px] items-center justify-center rounded-full bg-brand text-cream transition-colors duration-300 hover:bg-ink'
                                        >
                                            <Icon className='size-5' />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div
                        className={cn(
                            'mt-10 grid gap-8 border-t border-hairline pt-8 transition-opacity duration-500 sm:grid-cols-2 md:mt-12',
                            open ? 'opacity-100' : 'opacity-0'
                        )}
                        style={{ transitionDelay: open ? '480ms' : '0ms' }}
                    >
                        <div>
                            <p className={columnLabel}>
                                Packages
                            </p>

                            <ul className='mt-5 flex flex-col gap-2'>
                                {packageItems.slice(0, 4).map((item) => (
                                    <li key={item.slug}>
                                        <Link
                                            href='/packages'
                                            tabIndex={open ? 0 : -1}
                                            className='text-[19px] leading-[1.5] text-ink transition-colors hover:text-brand md:text-[22px]'
                                        >
                                            {item.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <p className={columnLabel}>
                                Cabins
                            </p>

                            <ul className='mt-5 flex flex-col gap-2'>
                                {roomDetails.map((room) => (
                                    <li key={room.slug}>
                                        <Link
                                            href={`/stay/${room.slug}`}
                                            tabIndex={open ? 0 : -1}
                                            className='text-[19px] leading-[1.5] text-ink transition-colors hover:text-brand md:text-[22px]'
                                        >
                                            {room.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
